import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, map } from 'rxjs/operators';
import {
  SearchRequest,
  SearchResponse,
  SearchResponseV3,
  SearchResultsProcessor,
} from './models/search.types';
import { API_BASE } from '../../constants/general.constants';

const DEFAULT_SEARCH_LIMIT = 10;

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private readonly http = inject(HttpClient);

  // Helper method to construct search-related URLs
  private createSearchUrl(resource?: string): string {
    let url = `${API_BASE}/search`;
    if (resource) {
      url += `/${resource}`;
    }
    return url;
  }

  private transformToV3Params(params: SearchRequest): any {
    // Map legacy names to new API names, preserving new names if already provided
    const projectSearch = params.projectSearch ?? false;
    const stepSearch = params.stepSearch ?? false;
    const projectId = params.projectId;
    const workflowId = params.workflowId;

    const { method, versions, query, offset, limit } = params;
    const result: any = { method, versions, query, projectSearch, stepSearch };
    if (typeof projectId !== 'undefined') result.projectId = projectId;
    if (typeof workflowId !== 'undefined') result.workflowId = workflowId as number;
    if (typeof offset !== 'undefined') result.offset = offset;
    if (typeof limit !== 'undefined') result.limit = limit;
    return result;
  }

  search(params: SearchRequest): Observable<SearchResponse> {
    const defaultParams: Partial<SearchRequest> = {
      method: 'like',
      versions: 'auto',
      offset: 0,
      limit: DEFAULT_SEARCH_LIMIT
    };

    const searchParams = this.transformToV3Params({ ...defaultParams, ...params } as SearchRequest);
    
    return this.http.post<SearchResponse | SearchResponseV3>(this.createSearchUrl(), searchParams).pipe(
      retry(1),
      map((response: any) => {
        // Accept both legacy and v3 shapes; normalize to legacy shape for existing consumers
        const normalized: SearchResponse =
          response && 'workflows' in response && 'steps' in response
            ? new SearchResultsProcessor().toLegacyResponse(response as SearchResponseV3)
            : {
                ...(response as SearchResponse),
                apis: response?.apis ?? [],
                endpoints: response?.endpoints ?? [],
              };

        return normalized;
      }),
      catchError(error => {
        console.error('Search failed:', error);
        return throwError(() => new Error(
          error.error?.message || 'Search failed. Please try again later.'
        ));
      })
    );
  }
}
